import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js'
import { isUUID } from '@debbie/common/dist/imports/data/helpers.js'
import { Entity } from '@debbie/common/dist/index.js'
import { useCase } from '@debbie/cortex/dist/cases/index.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { useCustomer } from '@debbie/cortex/dist/customers/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useUpdatesByRefId } from '@debbie/cortex/dist/updates/index.js'
import CaseLoader from '@debbie/iris/dist/components/CaseLoader.js'
import ArrowLeft from '@debbie/iris/dist/components/Icons/ArrowLeft.js'
import ArrowRight from '@debbie/iris/dist/components/Icons/ArrowRight.js'
import {
  Button,
  ButtonGroup,
  Content,
  PageBase,
  Panel,
  PlaceholderLoader,
  TopNav,
} from '@debbie/iris/dist/components/index.js'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Route, useRouteMatch } from 'react-router-dom'
import { styled } from 'styled-components'

import CaseSummary from '../../components/CaseSummary.js'
import { COLOR_SECONDARY, GRID_SPACING } from '../../theme.js'

const Case = (props: { id?: string; noRedirect?: true }) => {
  const match = useRouteMatch<{ ids: string }>()
  const ids = props.id ? [props.id] : match.params.ids.split(',')
  const id = ids[0]

  const caseIdLoader = useCase(id)
  const caseId = h.hasData(caseIdLoader) ? caseIdLoader.data.caseId : null
  const singleCaseLoader = useCase(caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null

  const nextCaseLoader = useCase(ids[1])
  const nextCaseId = h.hasData(nextCaseLoader)
    ? nextCaseLoader.data.caseId
    : null

  const customerLoader = useCustomer(singleCase?.customerId)
  const customer = h.hasData(customerLoader) ? customerLoader.data : null

  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null
  const updatesLoadable = useUpdatesByRefId(singleCase?.caseId)
  const updates = h.hasData(updatesLoadable) ? updatesLoadable.data : null
  const interactionsLoadable = useInteractionsByReference(
    Entity.Case,
    singleCase?.caseId,
    true,
  )
  const interactions = h.hasData(interactionsLoadable)
    ? interactionsLoadable.data
    : null

  const caseCollectorId =
    singleCase &&
    creditor &&
    getCollectorOnCase(
      singleCase.type,
      singleCase.collectorId,
      creditor.identityCollectorId,
      creditor.defaultCollectorId,
    )

  const collectorLoadable = useCollector(caseCollectorId)
  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null
  const caseError = h.isError(singleCaseLoader) ? singleCaseLoader.error : null
  const lang = useSelector((s: RootState) => s.lang)

  useEffect(() => {
    if (singleCase && isUUID(id)) {
      c.redirect.redirect(`/cases/${singleCase.id}`, { replace: true })
    }
  }, [singleCase])

  const caseIsLoading = !(
    singleCase &&
    customer &&
    updates &&
    interactions &&
    collector
  )

  return (
    <div>
      {caseId ? (
        <CaseLoader
          noRedirect={props.noRedirect}
          caseId={caseId}
          nextCaseId={nextCaseId}
        />
      ) : null}
      <TopNav />
      {caseError ? (
        <Content>
          <PageBase>
            <Panel>{caseError.message}</Panel>
          </PageBase>
        </Content>
      ) : singleCase && singleCase.deleted ? (
        <Content>
          <PageBase>
            <Panel>{lang.cases.caseHasBeenDeleted}</Panel>
          </PageBase>
        </Content>
      ) : (
        <Content fullHeight>
          <PageBase>
            {caseIsLoading ? (
              <PlaceholderLoader />
            ) : (
              <CaseSummary caseId={caseId} />
            )}
          </PageBase>
        </Content>
      )}
      {ids.length > 1 && (
        <CaseQueue>
          {lang.cases.casesLeft}: <strong>{ids.length - 1}</strong>
          <br />
          <br />
          <Route
            render={({ history }) => (
              <ButtonGroup>
                <Button
                  theme="secondary"
                  size="small"
                  onClick={() => {
                    history.goBack()
                  }}
                >
                  <ArrowLeft
                    style={{
                      verticalAlign: 'middle',
                    }}
                  />
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    history.push(
                      history.location.pathname.replace(
                        ids.join(','),
                        ids.slice(1, ids.length).join(','),
                      ),
                    )
                  }}
                >
                  #{ids[1]}{' '}
                  <ArrowRight
                    style={{
                      verticalAlign: 'middle',
                    }}
                  />
                </Button>
              </ButtonGroup>
            )}
          />
        </CaseQueue>
      )}
    </div>
  )
}

export default Case

const CaseQueue = styled.div`
  position: fixed;
  border-radius: 2px;
  box-shadow: 0 20px 70px -30px rgba(0, 0, 0, 0.15);
  bottom: 120px;
  right: 0px;
  background-color: ${COLOR_SECONDARY};
  padding: ${GRID_SPACING}px;
`
